import { MultiSelectSearch } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import SelectablePill from '../SelectablePill'

const propTypes = {
  /**
   * id of the multi select
   */
  id: PropTypes.string.isRequired,
  /**
   * Title of the field. Must be a FormattedMessage id
   */
  title: PropTypes.string,
  /**
   * options of the multi select
   */
  options: PropTypes.arrayOf(PropTypes.shape).isRequired,
  /**
   * Callback called when an option is selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Selected values
   */
  values: PropTypes.arrayOf(PropTypes.any), // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to format label content, should return an react node
   */
  formatOptionLabel: PropTypes.func,
  /**
   * validation errors messages
   */
  errors: PropTypes.arrayOf(PropTypes.string),
  /**
   * Class to style the title
   */
  titleClassName: PropTypes.string,
  /**
   * Indicates if field is required
   */
  required: PropTypes.bool,
  /**
   * Class to override default MultiSelectSearch styles
   */
  className: PropTypes.string,
  /**
   * When true, hides the selected value pills below the input
   */
  hidePills: PropTypes.bool,
}

const defaultProps = {
  title: null,
  values: [],
  formatOptionLabel: undefined,
  errors: [],
  titleClassName: '',
  required: false,
  className: '',
  hidePills: false,
}

function MultiSelectPillList(props) {
  const {
    id,
    title,
    options,
    onChange,
    values,
    formatOptionLabel,
    errors,
    titleClassName,
    required,
    className,
    hidePills,
    ...rest
  } = props

  const toOption = useMemo(() => {
    const result = {}
    options?.forEach((option) => {
      result[option.value] = option
    })
    return result
  }, [options])

  const selectedOptions = values.map((value) => toOption[value])

  const onRemoveValue = useCallback(
    (valueToRemove) => {
      const result = values.filter((item) => item !== valueToRemove)
      onChange(result)
    },
    [onChange, values],
  )

  const renderPillContent = (option) => (formatOptionLabel ? formatOptionLabel(option) : option?.label)

  // if value present is not in the list of the options show anyway in pill
  const createOptionForUnknownValue = (value) => ({ label: value, value })

  return (
    <div className="mb-3">
      {title && (
        <p className={clsx('mb-2', titleClassName)}>
          <FormattedMessage id={title} />
          &nbsp;
          {required && <span className="font-size-regular text-color-danger">*</span>}
        </p>
      )}

      <MultiSelectSearch
        id={id}
        options={options}
        onChange={onChange}
        value={selectedOptions}
        formatOptionLabel={formatOptionLabel}
        className={clsx('mb-2', errors?.length && 'select-error-border', className)}
        {...rest}
      />

      {errors?.map((errorMessage) => (
        <div key={errorMessage} className="text-color-danger mb-2">
          {errorMessage}
        </div>
      ))}

      {!hidePills && values?.length > 0 && (
        <div style={{ marginBottom: '-0.5rem' }}>
          {values.map((value) => (
            <SelectablePill key={value} selected onChange={() => onRemoveValue(value)}>
              {renderPillContent(toOption[value] || createOptionForUnknownValue(value))}
            </SelectablePill>
          ))}
        </div>
      )}
    </div>
  )
}

MultiSelectPillList.propTypes = propTypes
MultiSelectPillList.defaultProps = defaultProps

export default MultiSelectPillList
